'use client';
import { Alert, Box, Paper } from '@mui/material';
import { debounce } from '@mui/material/utils';
import type { JSONContent } from '@tiptap/core';
import { useCallback, useEffect, useState } from 'react';
import LoadingIndicator from '@/components/shared/loading-indicator';
import TextEditor from '@/components/shared/text-editor';
import { useChapterNote, useUpdateChapterNote } from '@/hooks';
import type { ChapterItemType } from '@/types';

const sx = {
  top: { xs: 56, sm: 64 },
  right: 0,
  left: { xs: 0, md: 'auto' },
  bottom: 50,
  p: 0,
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  height: {
    xs: 'calc(100vh - 56px - 50px)',
    sm: 'calc(100vh - 64px - 50px)',
  },
  width: { xs: '100%', md: '50vw' },
  borderRadius: 0,
  zIndex: 1,
};

export default function ChapterNote({
  chapterId,
  chapterType,
}: {
  chapterId: string;
  chapterType?: ChapterItemType;
}) {
  const [isSuccess, setIsSuccess] = useState(false);
  const { isLoading, data: chapterNote } = useChapterNote(chapterId, chapterType === 'Unit');
  const { mutate, isPending } = useUpdateChapterNote();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(
    debounce((newChapterNote) => {
      mutate(newChapterNote);
    }, 1200),
    [],
  );

  useEffect(() => {
    setIsSuccess(true);
  }, []);

  const onUpdate = (reference: string, note?: JSONContent) => {
    setIsSuccess(false);
    debounced({
      note,
      reference,
      referenceType: 'Unit',
    });
  };

  if (isLoading) {
    return (
      <Paper elevation={2} sx={sx}>
        <LoadingIndicator />
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={sx}>
      {chapterType !== 'Unit' && (
        <Alert severity="info" sx={{ m: 2 }}>
          In Übungen bieten wir keine Notizen an, um zu vermeiden, dass du dir selbst
          Lösungshinweise notierst.
        </Alert>
      )}
      {chapterType === 'Unit' && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
          <TextEditor
            onUpdate={onUpdate}
            content={chapterNote?.note}
            contentId={chapterNote?.reference ?? chapterId}
            isLoading={isLoading || isPending}
            isSuccess={isSuccess}
          />
        </Box>
      )}
    </Paper>
  );
}
